var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { Select } from "antd";
import { CompsChart } from "@ms/shared-components";
import { COMMON_CHART_SETTINGS } from "./config/index";
import { AXIS_LABEL_INTEGER } from "../../configs/tooltipCommon";
import { createRoomStatusRecentlyChartData, buildBaseChartDataFromFutureRoomStatusList, } from "./utils/roomStatusRecentlyChartTransformer";
import { getFutureRoomStatusListFromCache } from '../../utils/futureRoomStatusCache';
import { ROOM_STATUS_DISPLAY_MODE_OPTIONS } from "../../configs/selectOptionsConfig";
import { removeParenthesesFromRoomTypeName } from '../../utils/priceChannelDataTransformer';
import { createLogger } from '../../utils/logger';
import { EmptyDataPlaceholder } from "../EmptyDataPlaceholder";
var useRoomStatusRecently = function (baseChartDataFromApi, storeId) {
    var logger = useMemo(function () { return createLogger("RoomStatusRecently:".concat(storeId)); }, [storeId]);
    var _a = useState("已占用"), displayMode = _a[0], setDisplayMode = _a[1];
    var baseChartData = useMemo(function () {
        var _a, _b, _c, _d, _e, _f;
        var futureList = storeId != null ? getFutureRoomStatusListFromCache(storeId) : null;
        var source = baseChartDataFromApi;
        if (futureList === null || futureList === void 0 ? void 0 : futureList.length) {
            source = buildBaseChartDataFromFutureRoomStatusList(futureList);
        }
        var isArray = Array.isArray(source);
        var noValidStructure = !(source === null || source === void 0 ? void 0 : source.categories) || !(source === null || source === void 0 ? void 0 : source.dataset);
        if (isArray || noValidStructure) {
            if (isArray)
                logger.warn("远期房态 baseChartData 为数组（后端空数据），使用空结构");
            else
                logger.warn("未提供后端数据，返回空数据");
            return {
                categories: [],
                dataset: {
                    stayingRooms: [],
                    remainingRooms: {},
                    klineData: [],
                    averagePrice: [],
                    highestPrice: [],
                    lowestPrice: [],
                },
                axes: {
                    left: {
                        id: "left",
                        position: "left",
                        name: "房间数",
                        unit: "间",
                        max: 500,
                    },
                    right: {
                        id: "right",
                        position: "right",
                        name: "价格",
                        unit: "元",
                        min: 0,
                        max: 600,
                    },
                },
                baseSeries: [],
                comparison: { yearOverYear: [], weekOverWeek: [] },
            };
        }
        var dataset = __assign({}, source.dataset);
        if ((futureList === null || futureList === void 0 ? void 0 : futureList.length) &&
            ((_a = source === null || source === void 0 ? void 0 : source.categories) === null || _a === void 0 ? void 0 : _a.length) &&
            (baseChartDataFromApi === null || baseChartDataFromApi === void 0 ? void 0 : baseChartDataFromApi.dataset) &&
            !Array.isArray(baseChartDataFromApi) &&
            ((_b = baseChartDataFromApi.categories) === null || _b === void 0 ? void 0 : _b.length) === source.categories.length) {
            var apiDs = baseChartDataFromApi.dataset;
            if (Array.isArray(apiDs.klineData) &&
                apiDs.klineData.length === source.categories.length)
                dataset.klineData = apiDs.klineData;
            if (Array.isArray(apiDs.averagePrice) &&
                apiDs.averagePrice.length === source.categories.length)
                dataset.averagePrice = apiDs.averagePrice;
            if (Array.isArray(apiDs.highestPrice) &&
                apiDs.highestPrice.length === source.categories.length)
                dataset.highestPrice = apiDs.highestPrice;
            if (Array.isArray(apiDs.lowestPrice) &&
                apiDs.lowestPrice.length === source.categories.length)
                dataset.lowestPrice = apiDs.lowestPrice;
        }
        var remainingRooms = dataset.remainingRooms;
        var occupiedRooms = dataset.occupiedRooms;
        if (occupiedRooms &&
            typeof occupiedRooms === "object" &&
            !Array.isArray(occupiedRooms)) {
            Object.keys(occupiedRooms).forEach(function (roomType) {
                var arr = occupiedRooms[roomType];
                if (Array.isArray(arr))
                    dataset["occupied_".concat(roomType)] = arr;
            });
        }
        logger.debug("原始 dataset keys", Object.keys(dataset));
        logger.debug("stayingRooms 数据", dataset.stayingRooms);
        var roomTypeKeys = Object.keys(dataset).filter(function (key) {
            return key !== "stayingRooms" &&
                key !== "remainingRooms" &&
                key !== "occupiedRooms" &&
                key !== "klineData" &&
                key !== "averagePrice" &&
                key !== "highestPrice" &&
                key !== "lowestPrice" &&
                key !== "totalRooms" &&
                key !== "weekOverWeek" &&
                key !== "yearOverYear";
        });
        logger.debug("房型数据 keys", roomTypeKeys);
        roomTypeKeys.forEach(function (key) {
            logger.debug("".concat(key, " \u6570\u636E"), dataset[key]);
        });
        if (remainingRooms &&
            typeof remainingRooms === "object" &&
            !Array.isArray(remainingRooms)) {
            Object.keys(remainingRooms).forEach(function (roomType) {
                var remainingData = remainingRooms[roomType];
                if (Array.isArray(remainingData)) {
                    dataset["remaining_".concat(roomType)] = remainingData;
                }
            });
        }
        logger.debug("dataset 数据检查", {
            hasKlineData: !!dataset.klineData,
            klineDataLength: (_c = dataset.klineData) === null || _c === void 0 ? void 0 : _c.length,
            klineDataSample: (_d = dataset.klineData) === null || _d === void 0 ? void 0 : _d[0],
            hasAveragePrice: !!dataset.averagePrice,
            averagePriceLength: (_e = dataset.averagePrice) === null || _e === void 0 ? void 0 : _e.length,
        });
        var priceValues = [];
        if (Array.isArray(dataset.highestPrice))
            dataset.highestPrice.forEach(function (v) { return priceValues.push(v); });
        if (Array.isArray(dataset.averagePrice))
            dataset.averagePrice.forEach(function (v) { return priceValues.push(v); });
        if (Array.isArray(dataset.klineData)) {
            dataset.klineData.forEach(function (item) {
                if (Array.isArray(item))
                    item.forEach(function (v) { return priceValues.push(Number(v)); });
            });
        }
        var priceMax = priceValues.length ? Math.max.apply(Math, priceValues) : 0;
        var rightAxisMax = priceMax > 600 ? Math.ceil(priceMax * 1.05) : 600;
        var axes = __assign(__assign({}, source.axes), { right: __assign(__assign({}, (_f = source.axes) === null || _f === void 0 ? void 0 : _f.right), { max: rightAxisMax }) });
        var baseSeries = (source.baseSeries || []).map(function (series) {
            var updatedSeries = __assign({}, series);
            if (series.dataKey && series.dataKey.startsWith("remainingRooms.")) {
                var roomType = series.dataKey.replace("remainingRooms.", "");
                updatedSeries.dataKey = "remaining_".concat(roomType);
            }
            if (series.dataKey && series.dataKey.startsWith("occupiedRooms.")) {
                var roomType = series.dataKey.replace("occupiedRooms.", "");
                updatedSeries.dataKey = "occupied_".concat(roomType);
            }
            if (series.name) {
                updatedSeries.name = removeParenthesesFromRoomTypeName(series.name);
            }
            return updatedSeries;
        });
        return __assign(__assign({}, source), { dataset: dataset, baseSeries: baseSeries, axes: axes });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseChartDataFromApi, storeId]);
    var chartData = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g;
        var dataWithComparison = createRoomStatusRecentlyChartData(baseChartData);
        var axes = dataWithComparison.axes;
        var mergedAxes = __assign(__assign({}, axes), { left: __assign(__assign({}, axes === null || axes === void 0 ? void 0 : axes.left), { axisLabel: AXIS_LABEL_INTEGER, minInterval: 1 }), right: __assign(__assign({}, axes === null || axes === void 0 ? void 0 : axes.right), { axisLabel: AXIS_LABEL_INTEGER }) });
        var occupiedSeries = ((_a = dataWithComparison.series) === null || _a === void 0 ? void 0 : _a.filter(function (s) {
            return s.stack === "occupied" ||
                s.stackId === "occupied" ||
                s.stack === "used";
        })) || [];
        logger.debug("已占用组系列", occupiedSeries.map(function (s) { return ({
            id: s.id,
            name: s.name,
            dataKey: s.dataKey,
            stack: s.stack || s.stackId,
        }); }));
        occupiedSeries.forEach(function (s) {
            var _a;
            var data = (_a = dataWithComparison.dataset) === null || _a === void 0 ? void 0 : _a[s.dataKey];
            logger.debug("".concat(s.name, " (").concat(s.dataKey, ") \u6570\u636E"), data);
        });
        var klineSeries = (_b = dataWithComparison.series) === null || _b === void 0 ? void 0 : _b.find(function (s) { return s.type === "candlestick"; });
        if (!klineSeries) {
            logger.warn("K线图系列未找到", {
                allSeries: (_c = dataWithComparison.series) === null || _c === void 0 ? void 0 : _c.map(function (s) { return ({
                    id: s.id,
                    type: s.type,
                    dataKey: s.dataKey,
                }); }),
                baseSeries: (_d = baseChartData.baseSeries) === null || _d === void 0 ? void 0 : _d.map(function (s) { return ({
                    id: s.id,
                    type: s.type,
                    dataKey: s.dataKey,
                }); }),
            });
        }
        else {
            var dk = klineSeries.dataKey;
            logger.debug("K线图系列找到", {
                id: klineSeries.id,
                dataKey: dk,
                hasData: dk != null && !!((_e = dataWithComparison.dataset) === null || _e === void 0 ? void 0 : _e[String(dk)]),
            });
        }
        if (displayMode === "已占用") {
            var filteredSeries = ((_f = dataWithComparison.series) === null || _f === void 0 ? void 0 : _f.filter(function (s) {
                var _a, _b;
                var stackValue = s.stack || s.stackId;
                var dataKey = s.dataKey || "";
                var shouldKeep = stackValue === "occupied" ||
                    stackValue === "used" ||
                    s.id === "staying" ||
                    s.id === "stayingRooms" ||
                    ((_a = s.id) === null || _a === void 0 ? void 0 : _a.startsWith("used_")) ||
                    ((_b = s.id) === null || _b === void 0 ? void 0 : _b.startsWith("occupied_")) ||
                    dataKey.startsWith("occupiedRooms.") ||
                    dataKey.startsWith("occupied_") ||
                    s.id === "totalRooms" ||
                    s.type === "candlestick" ||
                    s.type === "line";
                return shouldKeep;
            })) || [];
            logger.debug("已占用模式 - 过滤后的系列", filteredSeries.map(function (s) { return ({
                id: s.id,
                name: s.name,
                type: s.type,
                dataKey: s.dataKey,
                axisId: s.axisId,
            }); }));
            var processedSeries = filteredSeries.map(function (s) { return (__assign(__assign({}, s), { name: s.name ? removeParenthesesFromRoomTypeName(s.name) : s.name })); });
            return __assign(__assign({}, dataWithComparison), { axes: mergedAxes, series: processedSeries });
        }
        else {
            var filteredSeries = ((_g = dataWithComparison.series) === null || _g === void 0 ? void 0 : _g.filter(function (s) {
                var _a;
                var stackValue = s.stack || s.stackId;
                var dataKey = s.dataKey || "";
                var shouldKeep = stackValue === "available" ||
                    stackValue === "remaining" ||
                    ((_a = s.id) === null || _a === void 0 ? void 0 : _a.startsWith("remaining_")) ||
                    dataKey.startsWith("remainingRooms.") ||
                    dataKey.startsWith("remaining_") ||
                    s.id === "totalRooms" ||
                    s.type === "candlestick" ||
                    s.type === "line";
                return shouldKeep;
            })) || [];
            var processedSeries = filteredSeries.map(function (s) { return (__assign(__assign({}, s), { name: s.name ? removeParenthesesFromRoomTypeName(s.name) : s.name })); });
            return __assign(__assign({}, dataWithComparison), { axes: mergedAxes, series: processedSeries });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseChartData, displayMode]);
    return {
        chartData: chartData,
        chartSettings: COMMON_CHART_SETTINGS,
        displayMode: displayMode,
        setDisplayMode: setDisplayMode,
    };
};
var RoomStatusRecently = function (_a) {
    var style = _a.style, storeId = _a.storeId, roomStatusRecentlyData = _a.roomStatusRecentlyData, onTitleClick = _a.onTitleClick, titleLeftAdornment = _a.titleLeftAdornment;
    var _b = useRoomStatusRecently(roomStatusRecentlyData === null || roomStatusRecentlyData === void 0 ? void 0 : roomStatusRecentlyData.baseChartData, storeId), chartData = _b.chartData, chartSettings = _b.chartSettings, displayMode = _b.displayMode, setDisplayMode = _b.setDisplayMode;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: "\n          .room-status-recently-header-selects .ant-select {\n            width: auto !important;\n            min-width: 120px !important;\n          }\n          .room-status-recently-header-selects .ant-select-selection-item {\n            white-space: nowrap !important;\n            overflow: visible !important;\n            text-overflow: clip !important;\n          }\n          .room-status-recently-header-selects .ant-select-dropdown {\n            min-width: fit-content !important;\n          }\n          .room-status-recently-header-selects .ant-select-dropdown .ant-select-item {\n            white-space: nowrap !important;\n          }\n        " }), _jsxs("div", __assign({ "data-component-id": "table:room-status-recently", style: __assign({ display: "flex", flexDirection: "column", width: 400, minHeight: 300, height: 300, border: "1px solid #f0f0f0", borderRadius: 4, background: "#fafafa", boxSizing: "border-box", overflow: "visible", flexShrink: 0 }, style) }, { children: [_jsxs("div", __assign({ style: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 12px",
                            borderBottom: "1px solid #f0f0f0",
                            background: "#fff",
                            flexShrink: 0,
                        } }, { children: [_jsxs("div", __assign({ style: { display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0 } }, { children: [titleLeftAdornment && (_jsx("div", __assign({ style: { marginLeft: -4, marginRight: 4, flexShrink: 0, display: "flex", alignItems: "center" } }, { children: titleLeftAdornment }))), _jsx("div", __assign({ onClick: onTitleClick, style: {
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: "#333",
                                            cursor: onTitleClick ? "pointer" : "default",
                                            userSelect: "none",
                                            transition: "color 0.3s ease",
                                        }, onMouseEnter: function (e) {
                                            if (onTitleClick) {
                                                e.currentTarget.style.color = "#1890ff";
                                            }
                                        }, onMouseLeave: function (e) {
                                            if (onTitleClick) {
                                                e.currentTarget.style.color = "#333";
                                            }
                                        }, title: onTitleClick ? "点击查看远期房态分析" : undefined }, { children: "\u8FDC\u671F\u623F\u6001" }))] })), _jsx("div", __assign({ className: "room-status-recently-header-selects", style: {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                } }, { children: _jsx(Select, { value: displayMode, onChange: function (value) { return setDisplayMode(value); }, size: "small", popupMatchSelectWidth: false, options: ROOM_STATUS_DISPLAY_MODE_OPTIONS }) }))] })), _jsx("div", __assign({ style: {
                            flex: 1,
                            minHeight: 200,
                            width: "100%",
                            padding: 0,
                            boxSizing: "border-box",
                            overflow: "visible",
                            position: "relative",
                            display: "flex",
                            background: "#fff",
                        } }, { children: chartData &&
                            Array.isArray(chartData.series) &&
                            chartData.series.length > 0 &&
                            Array.isArray(chartData.categories) &&
                            chartData.categories.length > 0 ? (_jsx(CompsChart, { type: "combo", data: chartData, settings: chartSettings, style: { width: "100%", height: "100%" } })) : (_jsx(EmptyDataPlaceholder, { description: "\u6682\u65E0\u6570\u636E" })) }))] }))] }));
};
export default RoomStatusRecently;
